import { immer } from 'zustand/middleware/immer'
import { createWithEqualityFn as create } from 'zustand/traditional'
import { shallow } from 'zustand/vanilla/shallow'

import {
	NotificationDto,
	NotificationStatus
} from '@wwt/entities/api/search-response'

import { createSelectors } from '@/shared/lib/createSelectors.ts'

import { wwtNotificationLog } from '../logger/wwtNotificationLog.ts'

export interface NotificationState {
	/**
	 * Map status:array of the notifications
	 */
	notifications: Record<NotificationStatus, NotificationDto[]>

	/**
	 * Map of notifications for fast delete/update
	 */
	notificationIdsMap: Record<
		NotificationStatus,
		NotificationDto['notificationId'][]
	>
}

export interface NotificationActions {
	/**
	 * Add new notifications
	 */
	addNotifications: (notifications: NotificationDto[]) => void

	/**
	 * Update notifications statuses
	 */
	updateNotificationsStatus: (
		mapForUpdate: Record<NotificationDto['notificationId'], NotificationStatus>
	) => void

	/**
	 * Update notification info by id
	 */
	updateNotification: (
		oldNotificationId: NotificationDto['notificationId'],
		newNotificationInfo: NotificationDto
	) => void

	/**
	 * Delete notification by id
	 */
	deleteNotification: (
		notificationId: NotificationDto['notificationId']
	) => void

	/**
	 * Clear store
	 */
	clearNotifications: () => void
}

export const useNotificationStoreBase = create<
	NotificationState & NotificationActions
>()(
	immer(set => ({
		notifications: {
			[NotificationStatus.NEW]: [],
			[NotificationStatus.SHOWN_POPUP]: [],
			[NotificationStatus.VIEWED]: [],
			[NotificationStatus.RESOLVED]: []
		},
		notificationIdsMap: {
			[NotificationStatus.NEW]: [],
			[NotificationStatus.SHOWN_POPUP]: [],
			[NotificationStatus.VIEWED]: [],
			[NotificationStatus.RESOLVED]: []
		},

		addNotifications: notifications =>
			set(state => {
				wwtNotificationLog('Add notifications', notifications)

				notifications.forEach(notification => {
					if (
						state.notificationIdsMap[notification.status] === undefined ||
						state.notifications[notification.status] === undefined
					) {
						wwtNotificationLog(
							'Found notification with not supported status',
							notification
						)

						throw new Error('Found notification with not supported status')
					}

					state.notificationIdsMap[notification.status].push(
						notification.notificationId
					)
					state.notifications[notification.status].push(notification)
				})
			}),

		updateNotificationsStatus: mapForUpdate =>
			set(state => {
				wwtNotificationLog('Update notifications statuses', mapForUpdate)

				const statuses = Object.keys(NotificationStatus) as Array<
					keyof typeof NotificationStatus
				>

				Object.entries(mapForUpdate).forEach(([notificationId, newStatus]) => {
					let notificationForMove: NotificationDto | undefined = undefined

					for (const status of statuses) {
						if (!state.notificationIdsMap[status].includes(notificationId)) {
							continue
						}

						notificationForMove = state.notifications[status].find(
							notification => notification.notificationId === notificationId
						)

						if (!notificationForMove) {
							wwtNotificationLog(
								'Notification for move not found on update notification status',
								notificationForMove,
								status,
								notificationId,
								state.notifications
							)

							throw new Error(
								'Notification for move not found on update notification status'
							)
						}

						state.notifications[status] = state.notifications[status].filter(
							notification => notification.notificationId !== notificationId
						)

						state.notificationIdsMap[status] = state.notificationIdsMap[
							status
						].filter(
							savedNotificationId => savedNotificationId !== notificationId
						)

						break
					}

					if (notificationForMove === undefined) {
						wwtNotificationLog(
							'Notification for move not found on update notification status after cycle'
						)

						throw new Error(
							'Notification for move not found on update notification status after cycle'
						)
					}

					state.notifications[newStatus].push(notificationForMove)
					state.notificationIdsMap[newStatus].push(
						notificationForMove.notificationId
					)
				})
			}),

		updateNotification: (oldNotificationId, newNotificationInfo) =>
			set(state => {
				wwtNotificationLog(
					'Update notification',
					oldNotificationId,
					newNotificationInfo
				)

				const statuses = Object.keys(NotificationStatus) as Array<
					keyof typeof NotificationStatus
				>

				for (const status of statuses) {
					if (!state.notificationIdsMap[status].includes(oldNotificationId)) {
						continue
					}

					const notificationForUpdate = state.notifications[status].find(
						notification => notification.notificationId === oldNotificationId
					)

					if (!notificationForUpdate) {
						wwtNotificationLog(
							'Notification for update not found',
							notificationForUpdate,
							status,
							oldNotificationId,
							state.notifications
						)

						throw new Error('Notification for update not found')
					}

					// If status is not same - delete old and add new
					if (notificationForUpdate.status !== newNotificationInfo.status) {
						state.notifications[status] = state.notifications[status].filter(
							notification => notification.notificationId !== oldNotificationId
						)

						state.notificationIdsMap[status] = state.notificationIdsMap[
							status
						].filter(
							savedNotificationId => savedNotificationId !== oldNotificationId
						)

						state.notifications[newNotificationInfo.status].push(
							newNotificationInfo
						)
						state.notificationIdsMap[newNotificationInfo.status].push(
							newNotificationInfo.notificationId
						)

						return
					}

					// If status is same - change old notification by oldNotificationId to new
					state.notifications[newNotificationInfo.status] = state.notifications[
						newNotificationInfo.status
					].map(notification => {
						if (notification.notificationId !== oldNotificationId) {
							return notification
						}

						return {
							...notification,
							...newNotificationInfo
						}
					})

					state.notificationIdsMap[newNotificationInfo.status] =
						state.notificationIdsMap[newNotificationInfo.status].map(
							notificationId => {
								if (notificationId !== oldNotificationId) {
									return notificationId
								}

								return newNotificationInfo.notificationId
							}
						)

					break
				}
			}),

		deleteNotification: notificationId =>
			set(state => {
				wwtNotificationLog('Delete notification', notificationId)

				const statuses = Object.keys(NotificationStatus) as Array<
					keyof typeof NotificationStatus
				>

				for (const status of statuses) {
					if (!state.notificationIdsMap[status].includes(notificationId)) {
						continue
					}

					state.notifications[status] = state.notifications[status].filter(
						notification => notification.notificationId !== notificationId
					)

					state.notificationIdsMap[status] = state.notificationIdsMap[
						status
					].filter(
						savedNotificationId => savedNotificationId !== notificationId
					)

					return
				}

				wwtNotificationLog(
					'Notification for delete with not found in store',
					notificationId,
					state.notifications,
					state.notificationIdsMap
				)
				throw new Error('Notification for delete with not found in store')
			}),

		clearNotifications: () =>
			set(state => {
				state.notifications = {
					[NotificationStatus.NEW]: [],
					[NotificationStatus.SHOWN_POPUP]: [],
					[NotificationStatus.VIEWED]: [],
					[NotificationStatus.RESOLVED]: []
				}

				state.notificationIdsMap = {
					[NotificationStatus.NEW]: [],
					[NotificationStatus.SHOWN_POPUP]: [],
					[NotificationStatus.VIEWED]: [],
					[NotificationStatus.RESOLVED]: []
				}
			})
	})),
	shallow
)

export const useNotificationStore = createSelectors(useNotificationStoreBase)
